import React, { useCallback, useEffect, useState } from 'react';
import {
    Accordion,
    FlexyButton,
    UploaderProvider,
    useModal,
} from '@europrocurement/flexy-components';
import type {
    Acompte,
    Collection,
    Reglement,
} from '@europrocurement/l2d-modules/providers/CollectionBillingProvider';
import { Box, Card, PaletteColor, Typography, useTheme } from '@mui/material';
import { addIcon } from '@europrocurement/l2d-icons';
import { useSnackbar } from 'notistack';
import {
    DocumentGed,
    useDossierDocumentFilesApi,
} from '@europrocurement/l2d-modules/modules/Dossiers/hooks';
import {
    DocumentPreviewProvider,
    useCurrentDossierCtx,
} from '@europrocurement/l2d-modules/modules/Dossiers/providers';
import CollectionProvision from '../../../CollectionProvision/CollectionProvision';
import ProvisionAdd from '../../../ProvisionAdd/ProvisionAdd';
import DocumentsModalContent from '../DocumentsModalContent/DocumentsModalContent';
import useDossierFacturationActions from '../../hooks/useDossierFacturationActions';

type EncaissementsSectionProps = {
    collections: Collection[];
    totalAcomptes: number;
    totalReglements: number;
    reglements: Array<Reglement>;
    acomptes: Array<Acompte>;
};

const EncaissementsSection: React.FunctionComponent<EncaissementsSectionProps> = function ({
    collections,
    totalAcomptes,
    totalReglements,
    reglements,
    acomptes,
}: EncaissementsSectionProps) {
    const { modalActions } = useModal();
    const { palette } = useTheme();
    const { getReglementsDocGed } = useDossierDocumentFilesApi();
    const { enqueueSnackbar } = useSnackbar();
    const { dossier } = useCurrentDossierCtx();
    const { createAcompte } = useDossierFacturationActions();

    const [reglementsDocs, setReglementsDocs] = useState<DocumentGed[]>([]);

    const refreshReglementsDocs = useCallback(async () => {
        if (!dossier) return;
        try {
            const reglementDocs = await getReglementsDocGed([dossier.id as number]);
            if (reglementDocs) {
                setReglementsDocs(reglementDocs);
            }
        } catch (err) {
            console.log(err);
            enqueueSnackbar('Une erreur est survenue lors de la récupération des fichiers', {
                variant: 'error',
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dossier?.id]);

    const handleCreateAcompte = async (acompte: Acompte, files?: File[]) => {
        const collection = collections[0];

        if (!collection) {
            return;
        }

        modalActions.close();
        await createAcompte(collection, acompte, files, dossier);

        if (files && files.length > 0) {
            await refreshReglementsDocs();
        }
    };

    useEffect(() => {
        refreshReglementsDocs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleAddAttachmentFile = useCallback(
        (reglementId?: number) => {
            if (dossier && reglementId) {
                modalActions.call(
                    <DocumentPreviewProvider>
                        <DocumentsModalContent
                            dossier={dossier}
                            reglementsDocGed={reglementsDocs}
                            reglementId={reglementId}
                            setReglementsDocs={setReglementsDocs}
                        />
                    </DocumentPreviewProvider>,
                    true,
                    true,
                );
            }
        },
        [modalActions, reglementsDocs, dossier],
    );

    return (
        <Card
            id="reglements"
            sx={{ boxShadow: 'none', padding: 0, mx: 0 }}
        >
            <Accordion
                sx={{
                    boxShadow: 'none',
                }}
                title="Règlements"
                titlesSx={{
                    fontWeight: '600',
                    boxShadow: 'none',
                }}
                headerSx={{ boxShadow: 'none' }}
                expanded
                expandColor={palette.text.primary as unknown as PaletteColor}
                expandLabel={
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '10px',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'space-evenly',
                            }}
                        >
                            <Typography
                                variant="l2dh7"
                                sx={{ marginRight: '10px', fontSize: '0.875rem' }}
                            >
                                Total réglé :
                            </Typography>
                            <Typography
                                variant="l2dh7"
                                sx={{ color: 'text.primary' }}
                            >
                                {` ${totalAcomptes + totalReglements} €`}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'space-evenly',
                            }}
                        >
                            <Typography
                                variant="l2dh7"
                                sx={{ marginRight: '10px', fontSize: '0.875rem' }}
                            >
                                Crédit restant :
                            </Typography>
                            <Typography
                                variant="l2dh7"
                                sx={{ color: 'text.secondary' }}
                            >
                                {` ${totalAcomptes}
                                    €`}
                            </Typography>
                        </Box>
                    </Box>
                }
                footerActions={
                    collections.length === 1
                        ? [
                              <Box
                                  sx={{
                                      display: 'flex',
                                      flexDirection: 'row',
                                      justifyContent: 'end',
                                      alignItems: 'center',
                                      gap: '10px',
                                  }}
                              >
                                  <FlexyButton
                                      variant="outlined"
                                      onClick={() => {
                                          modalActions.call(
                                              <UploaderProvider>
                                                  <ProvisionAdd
                                                      onAddProvision={handleCreateAcompte}
                                                      collectionId={collections[0].id as number}
                                                      sx={{ maxWidth: '600px' }}
                                                  />
                                              </UploaderProvider>,
                                              true,
                                              false,
                                          );
                                      }}
                                      icon={addIcon}
                                      label="Ajouter un règlement"
                                  />
                              </Box>,
                          ]
                        : []
                }
            >
                <CollectionProvision
                    acomptes={[...acomptes]}
                    reglements={[...reglements]}
                    reglementsDocs={reglementsDocs}
                    handleAddAttachmentFile={handleAddAttachmentFile}
                />
            </Accordion>
        </Card>
    );
};

export default EncaissementsSection;
